/**
 * product-gallery-zoom.js — PDP zoomed media gallery (fullscreen lightbox).
 *
 * <product-gallery-zoom> is rendered ONCE per page at the section root (see
 * snippets/product-gallery-zoom.liquid for why that placement is load-bearing).
 *
 * TRIGGER CONTRACT — any element with data-zoom-open="{index}" opens the overlay at that
 * media index. Listened for by DELEGATED click, so the inline gallery can be built later,
 * re-rendered, or swapped without re-binding anything here.
 *
 * THE ONE-WAY RULE:
 *   The MAIN viewport drives the indicator.
 *   - scroll/swipe the main  → scrollspy moves the indicator
 *   - tap a thumbnail        → the MAIN scrolls, and the indicator moves BECAUSE the main moved
 *   Thumbnails are the buttons. The indicator is a readout.
 *
 * LAYER 2 BREAKS THAT RULE — on purpose, and only in the desktop layout: the indicator also
 * becomes a drag handle, so it both reports position AND sets it. That two-way binding is the
 * whole source of jitter risk, which is why `isDragging` / `suppressSpy` exist. Every path that
 * writes the active index checks them. On phone the indicator only ever listens, so the loop
 * cannot form there at all.
 *
 * A11Y NOTE: the indicator stays aria-hidden and does NOT get role="slider". Per the spec, the
 * THUMBNAILS are the accessible control at every breakpoint (real <button>s with labels and
 * aria-current); the drag is a pointer-only enhancement layered on top. Keyboard users get
 * arrow/Home/End navigation on the dialog instead.
 *
 * Spec: docs/00-features/product/media-gallery/ (01-research-and-brainstorm.md, 02-build-log.md)
 */
(function () {
  if (customElements.get('product-gallery-zoom')) return;

  // Must match the @media (min-width: 51.25rem) line in the snippet's stylesheet.
  var DESKTOP_MQ = '(min-width: 51.25rem)';
  var FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var EDGE_ZONE = 28; // px from the rail edge that triggers auto-scroll
  var EDGE_SPEED = 8; // px per frame
  var SETTLE_MS = 120; // debounce for "the programmatic scroll has finished"

  // How hard the main column chases the drag target, per frame. THE FEEL DIAL:
  //   1    = instant (no smoothing at all)
  //   0.22 = ~300ms to settle — reads as weight
  //   0.08 = floaty / heavy
  // NOTE: this is a hand-rolled lerp on purpose. scrollTo({behavior:'smooth'}) cannot be used
  // for live drag-follow — consecutive smooth scrolls restart from the current position and
  // fight each other, and their duration is UA-controlled. See the header note.
  var DRAG_DAMP = 0.22;

  // --- Mobile 2nd-zoom (transform pan + pinch) ---
  var TAP_SLOP = 10; // px of movement that turns a tap into a pan (so a paging swipe ≠ a zoom tap)
  var TAP_ZOOM = 2; // tap zoom level (200%, centre-anchored)
  var MAX_ZOOM = 4; // pinch ceiling

  function clamp(n, min, max) {
    return Math.max(min, Math.min(n, max));
  }

  class ProductGalleryZoom extends HTMLElement {
    connectedCallback() {
      this.template = this.querySelector('[data-pgz-template]');
      this.populated = false;
      this.isOpen = false;
      this.index = 0;
      this.lastFocused = null;

      // --- Layer 2 state ---
      this.isDragging = false; // the box is driving the main
      this.suppressSpy = false; // a smooth (UA) scroll is in flight
      this.scrubbing = false; // our own lerp loop is animating the main
      this.scrollTarget = 0;
      this.scrollRAF = null;
      this.edgeDir = 0;
      this.edgeRAF = null;
      this.lastPointerY = 0;

      // --- Mobile 2nd-zoom state (phone layout only) ---
      this.zItem = null; // the item currently transform-zoomed (null = none)
      this.zImg = null;
      this.zScale = 1;
      this.zTx = 0;
      this.zTy = 0;
      this.zW = this.zH = this.zVw = this.zVh = this.zCx = this.zCy = 0; // cached at activation
      this.zPointers = new Map(); // active pointerId -> {x,y}
      this.zMoved = false; // did the gesture move past TAP_SLOP (or get cancelled)?
      this.zStartDist = 0;
      this.zStartScale = 1;
      this.zTapItem = null;
      this.zTapX = this.zTapY = this.zPanLastX = this.zPanLastY = 0;
      this.zRaf = 0;

      this.desktop = window.matchMedia(DESKTOP_MQ);

      this.onDocClick = this.onDocClick.bind(this);
      this.onDocKeydown = this.onDocKeydown.bind(this);
      this.onSelfClick = this.onSelfClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onMainScroll = this.onMainScroll.bind(this);
      this.onUserScrollIntent = this.onUserScrollIntent.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.edgeTick = this.edgeTick.bind(this);
      this.scrollTick = this.scrollTick.bind(this);
      this.refreshScrollHints = this.refreshScrollHints.bind(this);
      this.onImgPointerDown = this.onImgPointerDown.bind(this);
      this.onImgPointerMove = this.onImgPointerMove.bind(this);
      this.onImgPointerUp = this.onImgPointerUp.bind(this);
      this.onImgPointerCancel = this.onImgPointerCancel.bind(this);
      this.onGestureStart = this.onGestureStart.bind(this);

      // Delegated → triggers can appear/disappear at any time (inline gallery TBD).
      document.addEventListener('click', this.onDocClick);
      this.addEventListener('click', this.onSelfClick);
      if (this.desktop.addEventListener) {
        this.desktop.addEventListener('change', this.onBreakpointChange);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onDocKeydown);
      if (this.desktop.removeEventListener) {
        this.desktop.removeEventListener('change', this.onBreakpointChange);
      }
      this.endDrag();
      this.tzReset(false);
      this.teardownSpy();
      this.unlockScroll();
    }

    /* ------------------------------------------------------------------ triggers */

    onDocClick(event) {
      var trigger = event.target.closest && event.target.closest('[data-zoom-open]');
      if (!trigger) return;
      event.preventDefault();
      var i = parseInt(trigger.getAttribute('data-zoom-open'), 10);
      this.open(isNaN(i) ? 0 : i);
    }

    onSelfClick(event) {
      if (event.target.closest('[data-pgz-close]')) {
        this.close();
        return;
      }
      var thumb = event.target.closest('[data-pgz-thumb]');
      if (thumb) {
        // Thumbnails are the buttons. We move the MAIN; the indicator follows as a consequence.
        this.clearZoom();
        this.goTo(parseInt(thumb.getAttribute('data-pgz-index'), 10) || 0, 'smooth');
        return;
      }

      // Click an IMAGE to toggle zoom. DESKTOP only — mobile handles tap-to-zoom in the pointer
      // handlers (so a paging swipe isn't mistaken for a zoom tap). Matching on .pmi__img excludes
      // video (<product-media-video> renders a <video>, never a .pmi__img), which keeps its own
      // play/pause click at every breakpoint.
      var img = event.target.closest('.pmi__img');
      if (img && this.desktop.matches) this.toggleImageZoom(img.closest('[data-pgz-item]'));
    }

    onDocKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab') {
        this.trapFocus(event);
        return;
      }

      // Keyboard navigation — the accessible equivalent of dragging the indicator.
      var last = this.items ? this.items.length - 1 : 0;
      var target = null;
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') target = this.index + 1;
      else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') target = this.index - 1;
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = last;

      if (target !== null) {
        event.preventDefault();
        // Navigating away resets zoom — clear BEFORE goTo so it measures the new layout.
        this.clearZoom();
        this.goTo(clamp(target, 0, last), 'smooth');
      }
    }

    onBreakpointChange() {
      // Dropping to the phone layout mid-drag would leave a dangling capture + rAF loop.
      if (!this.desktop.matches) this.endDrag();
      // Neither zoom mode survives a breakpoint flip (desktop = scroll-fill, mobile = transform).
      this.clearZoom();
    }

    /* ------------------------------------------------------------------ lifecycle */

    /** Clone the inert <template> in. Idempotent — images only fetch on first open. */
    populate() {
      if (this.populated || !this.template || !this.template.content) return;
      this.appendChild(this.template.content.cloneNode(true));
      this.populated = true;

      this.nav = this.querySelector('[data-pgz-nav]');
      this.rail = this.querySelector('[data-pgz-rail]');
      this.track = this.querySelector('[data-pgz-track]');
      this.indicator = this.querySelector('[data-pgz-indicator]');
      this.main = this.querySelector('[data-pgz-main]');
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-pgz-item]'));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-pgz-thumb]'));

      // Live up/down chevron affordance as the rail scrolls (user scroll, active-thumb
      // scrollIntoView, and drag edge-scroll all fire this).
      if (this.rail) this.rail.addEventListener('scroll', this.refreshScrollHints, { passive: true });

      if (this.indicator) {
        this.indicator.addEventListener('pointerdown', this.onPointerDown);
        this.indicator.addEventListener('pointermove', this.onPointerMove);
        this.indicator.addEventListener('pointerup', this.onPointerUp);
        this.indicator.addEventListener('pointercancel', this.onPointerUp);
      }
      if (this.main) {
        this.main.addEventListener('scroll', this.onMainScroll, { passive: true });
        this.main.addEventListener('wheel', this.onUserScrollIntent, { passive: true });
        this.main.addEventListener('touchstart', this.onUserScrollIntent, { passive: true });
        // Mobile 2nd-zoom (transform pan + pinch). Delegated on main; gated to phone in the
        // handlers. pointermove is NON-passive so a pan/pinch can preventDefault the pager.
        this.main.addEventListener('pointerdown', this.onImgPointerDown);
        this.main.addEventListener('pointermove', this.onImgPointerMove, { passive: false });
        this.main.addEventListener('pointerup', this.onImgPointerUp);
        this.main.addEventListener('pointercancel', this.onImgPointerCancel);
      }
      // iOS: block native pinch-zoom on the overlay (touch-action covers most; belt-and-braces).
      this.addEventListener('gesturestart', this.onGestureStart);

      this.setupSpy();
    }

    open(index) {
      if (this.isOpen) return;
      this.lastFocused = document.activeElement;

      this.populate();
      if (!this.main) return;

      this.isOpen = true;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      this.lockScroll();
      document.addEventListener('keydown', this.onDocKeydown);

      // Set the readout immediately so there's no flash of index 0 before the spy fires.
      this.setActive(index);

      // Defer a frame: the reserved aspect-ratio boxes need computed heights before we scroll,
      // otherwise the first-open jump lands short (heights come from aspect-ratio, not from
      // decoded images).
      var self = this;
      requestAnimationFrame(function () {
        self.goTo(index, 'auto');
        self.refreshScrollHints();
        var close = self.querySelector('[data-pgz-close]');
        if (close) close.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.isOpen) return;

      // REVERSE CONTRACT (Zoom → Main). Tell the inline gallery which media we ended on, so its
      // mobile Embla carousel lands on the same index. Dispatched here — at the start of close,
      // while the overlay still covers — so the main gallery's instant jump is never seen. This is
      // the "event out" symmetric to the data-zoom-open="{index}" attribute that opened us; neither
      // file references the other. Fires at every breakpoint; the gallery ignores it on desktop.
      document.dispatchEvent(new CustomEvent('pgz:close', { detail: { index: this.index } }));

      this.endDrag();
      this.stopScrub();
      this.clearZoom(); // reopen always starts in the fit state
      this.isOpen = false;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.unlockScroll();
      document.removeEventListener('keydown', this.onDocKeydown);

      this.querySelectorAll('video').forEach(function (v) {
        if (!v.paused) v.pause();
      });

      // Focus return. If the shopper browsed to a DIFFERENT media, the originally-clicked thumbnail
      // is no longer the logical anchor (ARIA APG "more logical element" exception) — return focus
      // to the NOW-active trigger instead, matching where the galleries now sit. Falls back to the
      // opener for media that has no trigger (videos aren't wrapped in a data-zoom-open button).
      var activeTrigger = document.querySelector('[data-zoom-open="' + this.index + '"]');
      var returnTo = activeTrigger || this.lastFocused;
      if (returnTo && returnTo.focus) {
        returnTo.focus({ preventScroll: true });
      }
    }

    /* ------------------------------------------------- Layer 2: drag the indicator */

    /** Distance between two thumb stops. Measured, so tuning the CSS vars can't desync it. */
    stepSize() {
      if (!this.thumbs || !this.thumbs.length) return 0;
      if (this.thumbs.length > 1) return this.thumbs[1].offsetTop - this.thumbs[0].offsetTop;
      return this.thumbs[0].offsetHeight;
    }

    onPointerDown(event) {
      // Gated to the desktop layout. CSS also sets pointer-events:none on phone, so this is
      // belt-and-braces against a breakpoint change racing the gesture.
      if (!this.desktop.matches || !this.isOpen) return;

      event.preventDefault();

      // Scrubbing assumes uniform viewport-height items. A zoomed item is many viewports tall,
      // which would make the drag map to wildly wrong positions — so collapse it first.
      this.clearZoom();

      this.isDragging = true;
      this.classList.add('is-dragging');
      this.lastPointerY = event.clientY;

      // Preserve the grab point so the box doesn't jump under the cursor on pointerdown.
      var rect = this.indicator.getBoundingClientRect();
      this.grabOffset = event.clientY - rect.top;

      if (this.indicator.setPointerCapture) {
        try {
          this.indicator.setPointerCapture(event.pointerId);
        } catch (e) {
          /* capture is best-effort */
        }
      }
    }

    onPointerMove(event) {
      if (!this.isDragging) return;
      event.preventDefault();
      this.lastPointerY = event.clientY;
      this.updateFromPointer();
      this.updateEdgeScroll();
    }

    onPointerUp(event) {
      if (!this.isDragging) return;
      if (this.indicator.releasePointerCapture) {
        try {
          this.indicator.releasePointerCapture(event.pointerId);
        } catch (e) {
          /* no-op */
        }
      }
      this.endDrag();
    }

    endDrag() {
      if (!this.isDragging) {
        this.stopEdgeScroll();
        return;
      }
      this.isDragging = false;
      this.classList.remove('is-dragging');
      this.stopEdgeScroll();
    }

    /**
     * Map the current pointer position to a thumb stop and drive the main column.
     * Called from pointermove AND from the edge-scroll rAF — because when the rail scrolls
     * under a stationary finger, the same clientY now points at a different thumb.
     */
    updateFromPointer() {
      if (!this.track || !this.items.length) return;
      var step = this.stepSize();
      if (!step) return;

      var trackTop = this.track.getBoundingClientRect().top;
      var y = this.lastPointerY - this.grabOffset - trackTop;
      var i = clamp(Math.round(y / step), 0, this.items.length - 1);

      if (i !== this.index) {
        this.setActive(i);
        // LIVE: the main follows every snap step, via our own lerp so rapid steps re-aim one
        // continuous animation instead of stacking competing ones.
        this.scrubTo(i);
      }
    }

    /* ------------------------------------------------------------ image zoom (fit/fill) */

    /**
     * Toggle one image between "fit" (letterboxed in a viewport-height item) and "filled"
     * (full-bleed, natural ratio, taller than the screen so you pan down it).
     *
     * The re-anchor is the important part: growing an item shoves everything below it down, so
     * without re-scrolling you'd be thrown to a different part of the gallery. We re-run goTo()
     * on the NEXT frame, after layout has taken the new height into account.
     */
    toggleImageZoom(item) {
      if (!item || !this.main) return;
      var i = parseInt(item.getAttribute('data-pgz-index'), 10) || 0;

      var zoomedIn = item.classList.toggle('is-zoomed');
      this.main.classList.toggle('has-zoom', !!this.querySelector('.pgz__item.is-zoomed'));

      // Any in-flight animation is now aiming at a stale position.
      this.stopScrub();

      var self = this;
      requestAnimationFrame(function () {
        if (zoomedIn) {
          // Land 25% INTO the now-taller image (not its top edge). Kept OUT of topFor() — that's
          // shared by open/jump/drag; this offset is zoom-only. Clamped to the scroll range.
          var max = Math.max(0, self.main.scrollHeight - self.main.clientHeight);
          var target = self.topFor(i) + item.offsetHeight * 0.25;
          self.main.scrollTo({ top: clamp(target, 0, max), behavior: 'auto' });
        } else {
          self.goTo(i, 'auto'); // un-zoom → back to the fit top
        }
      });
    }

    /** Drop every zoomed item (desktop scroll-fill AND mobile transform). Returns true if changed. */
    clearZoom() {
      var changed = false;
      var zoomed = this.querySelectorAll('.pgz__item.is-zoomed');
      if (zoomed.length) {
        Array.prototype.forEach.call(zoomed, function (el) {
          el.classList.remove('is-zoomed');
        });
        if (this.main) this.main.classList.remove('has-zoom');
        changed = true;
      }
      if (this.zItem) {
        this.tzReset(false); // instant (navigating away / closing)
        changed = true;
      }
      return changed;
    }

    /* ===================== MOBILE 2nd-zoom: transform pan + pinch =====================
       Phone layout only (desktop uses the scroll-fill .is-zoomed path above). One composited
       transform on the .pmi__img: tap → 2x centre-anchored; pinch → focal scale (finger midpoint)
       up to MAX_ZOOM; one-finger drag → pan, clamped to keep the image covering the viewport.
       No layout change, so nothing overflows the column; the vertical pager is locked by flipping
       the image's touch-action to `none` + hiding main overflow (.pgz--tzoom). Full rationale in
       docs/00-features/product/media-gallery/02-build-log.md §5.6. */

    onImgPointerDown(event) {
      if (this.desktop.matches || !this.isOpen) return;
      var onImg = event.target.closest && event.target.closest('.pmi__img');
      if (!onImg && !this.zItem) return; // engage only on an image (or while already zoomed)

      this.zPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.zPointers.size === 1) {
        this.zTapItem = onImg ? onImg.closest('[data-pgz-item]') : this.zItem;
        this.zTapX = this.zPanLastX = event.clientX;
        this.zTapY = this.zPanLastY = event.clientY;
        this.zMoved = false;
        // Already zoomed → we own the pan: capture now so the pager can't steal it. At fit we do
        // NOT capture, so a single-finger swipe still pages natively.
        if (this.zItem && this.zScale > 1) this.tzCapture(event.pointerId);
      } else if (this.zPointers.size === 2) {
        // Second finger → pinch. Commit to owning every gesture.
        this.zMoved = true; // a pinch is never a tap
        var start = this.zItem || (onImg && onImg.closest('[data-pgz-item]')) || this.zTapItem;
        if (!this.zItem && start) this.tzActivate(start);
        if (!this.zItem) {
          this.zPointers.delete(event.pointerId);
          return;
        }
        var self = this;
        this.zPointers.forEach(function (v, id) {
          self.tzCapture(id);
        });
        var p = this.tzPoints();
        this.zStartDist = this.tzDist(p[0], p[1]);
        this.zStartScale = this.zScale;
      }
    }

    onImgPointerMove(event) {
      if (!this.zPointers.has(event.pointerId)) return;
      var pt = this.zPointers.get(event.pointerId);
      pt.x = event.clientX;
      pt.y = event.clientY;

      if (
        Math.abs(event.clientX - this.zTapX) > TAP_SLOP ||
        Math.abs(event.clientY - this.zTapY) > TAP_SLOP
      ) {
        this.zMoved = true;
      }

      if (this.zPointers.size >= 2 && this.zItem) {
        event.preventDefault();
        var pts = this.tzPoints();
        var dist = this.tzDist(pts[0], pts[1]);
        if (this.zStartDist > 0) {
          var s = clamp((this.zStartScale * dist) / this.zStartDist, 1, MAX_ZOOM);
          this.tzScaleAround(s, (pts[0].x + pts[1].x) / 2, (pts[0].y + pts[1].y) / 2);
        }
      } else if (this.zPointers.size === 1 && this.zItem && this.zScale > 1) {
        event.preventDefault();
        this.zTx += event.clientX - this.zPanLastX;
        this.zTy += event.clientY - this.zPanLastY;
        this.zPanLastX = event.clientX;
        this.zPanLastY = event.clientY;
        this.tzClamp();
        this.tzRender();
      }
      // else: fit + single finger → do NOT preventDefault; the browser pages natively.
    }

    onImgPointerUp(event) {
      if (!this.zPointers.has(event.pointerId)) return;
      this.zPointers.delete(event.pointerId);
      this.tzRelease(event.pointerId);

      if (this.zPointers.size === 1) {
        // one finger remains after a pinch → continue as a pan from its current spot
        var rem = this.tzPoints()[0];
        if (rem) {
          this.zPanLastX = rem.x;
          this.zPanLastY = rem.y;
        }
        return;
      }
      if (this.zPointers.size > 0) return;

      if (!this.zMoved) {
        this.tzTapToggle(this.zTapItem); // clean tap → toggle zoom
        return;
      }
      if (this.zItem) {
        if (this.zScale <= 1.02) {
          this.tzReset(true); // pinched back to fit → exit
        } else {
          this.tzClamp();
          this.tzRender();
          this.tzUpgradeHiRes(); // settle crisp
        }
      }
    }

    onImgPointerCancel(event) {
      if (!this.zPointers.has(event.pointerId)) return;
      this.zPointers.delete(event.pointerId);
      this.tzRelease(event.pointerId);
      // A cancelled pointer means the browser took the gesture (e.g. paging) — never a tap.
      this.zMoved = true;
    }

    onGestureStart(event) {
      if (!this.desktop.matches && this.isOpen) event.preventDefault();
    }

    /* --- helpers --- */

    tzCapture(id) {
      try {
        this.main.setPointerCapture(id);
      } catch (e) {
        /* best-effort */
      }
      if (this.zImg) this.zImg.style.touchAction = 'none';
    }

    tzRelease(id) {
      try {
        this.main.releasePointerCapture(id);
      } catch (e) {
        /* no-op */
      }
    }

    tzPoints() {
      var a = [];
      this.zPointers.forEach(function (v) {
        a.push(v);
      });
      return a;
    }

    tzDist(a, b) {
      var dx = a.x - b.x;
      var dy = a.y - b.y;
      return Math.sqrt(dx * dx + dy * dy);
    }

    /** Cache the fit box + viewport and enter transform-zoom. Call at scale 1 (fit). */
    tzActivate(item) {
      var img = item && item.querySelector('.pmi__img');
      if (!img) return false;
      this.zItem = item;
      this.zImg = img;
      var r = img.getBoundingClientRect(); // fit box (no transform yet)
      this.zW = r.width;
      this.zH = r.height;
      this.zCx = r.left + r.width / 2; // fit-box centre in viewport coords (transform-origin 50%)
      this.zCy = r.top + r.height / 2;
      this.zVw = this.main.clientWidth;
      this.zVh = this.main.clientHeight;
      this.zScale = 1;
      this.zTx = 0;
      this.zTy = 0;
      this.classList.add('pgz--tzoom');
      item.classList.add('pgz__item--tzoom');
      img.style.touchAction = 'none';
      return true;
    }

    tzRender() {
      if (this.zRaf || !this.zImg) return;
      var self = this;
      this.zRaf = requestAnimationFrame(function () {
        self.zRaf = 0;
        if (self.zImg) {
          self.zImg.style.transform =
            'translate3d(' + self.zTx + 'px,' + self.zTy + 'px,0) scale(' + self.zScale + ')';
        }
      });
    }

    tzClamp() {
      // Keep the image covering the viewport on the overflowing axis; recentre on the other.
      var maxTx = Math.max(0, (this.zW * this.zScale - this.zVw) / 2);
      var maxTy = Math.max(0, (this.zH * this.zScale - this.zVh) / 2);
      this.zTx = clamp(this.zTx, -maxTx, maxTx);
      this.zTy = clamp(this.zTy, -maxTy, maxTy);
    }

    /** Focal pinch: scale toward viewport point (fx,fy), keeping the content under it fixed. */
    tzScaleAround(newScale, fx, fy) {
      var gx = fx - this.zCx;
      var gy = fy - this.zCy;
      var ratio = newScale / this.zScale;
      this.zTx = gx - (gx - this.zTx) * ratio;
      this.zTy = gy - (gy - this.zTy) * ratio;
      this.zScale = newScale;
      this.tzClamp();
      this.tzRender();
    }

    /** Tap: fit → 2x (centre-anchored); zoomed → back to fit. Animated. */
    tzTapToggle(item) {
      if (this.zItem && this.zScale > 1) {
        this.tzReset(true);
        return;
      }
      if (!this.tzActivate(item)) return;
      this.zScale = TAP_ZOOM;
      this.zTx = 0;
      this.zTy = 0; // centre-anchored
      this.tzClamp();
      this.tzAnimateIn();
    }

    /** Write the current transform under a CSS transition, with the will-change discipline that
     *  avoids the static-scale blur (will-change ON only for the animation, OFF at rest). */
    tzAnimateIn() {
      var img = this.zImg;
      if (!img) return;
      img.style.willChange = 'transform';
      img.classList.add('pgz__img--anim');
      img.style.transform =
        'translate3d(' + this.zTx + 'px,' + this.zTy + 'px,0) scale(' + this.zScale + ')';
      var self = this;
      var done = function () {
        img.style.willChange = 'auto'; // OFF at rest → re-raster crisp
        img.classList.remove('pgz__img--anim');
        img.removeEventListener('transitionend', done);
        clearTimeout(t);
        if (self.zItem && self.zScale > 1) self.tzUpgradeHiRes();
      };
      var t = setTimeout(done, 400); // fallback if transitionend is missed
      img.addEventListener('transitionend', done);
    }

    /** Leave transform-zoom. animate=true for a tap-exit; false for instant (navigation/close). */
    tzReset(animate) {
      if (!this.zItem) return;
      var img = this.zImg;
      var item = this.zItem;
      if (this.zRaf) {
        cancelAnimationFrame(this.zRaf);
        this.zRaf = 0;
      }
      this.zScale = 1;
      this.zTx = 0;
      this.zTy = 0;
      this.zPointers.clear();
      this.classList.remove('pgz--tzoom');

      var finish = function () {
        img.style.transform = '';
        img.style.willChange = '';
        img.style.touchAction = '';
        img.classList.remove('pgz__img--anim');
        item.classList.remove('pgz__item--tzoom');
      };

      if (animate && img) {
        img.style.willChange = 'transform';
        img.classList.add('pgz__img--anim');
        img.style.transform = 'translate3d(0,0,0) scale(1)';
        var done = function () {
          img.removeEventListener('transitionend', done);
          clearTimeout(t);
          finish();
        };
        var t = setTimeout(done, 400);
        img.addEventListener('transitionend', done);
      } else if (img) {
        finish();
      }

      this.zItem = null;
      this.zImg = null;
    }

    /** Lazy hi-res upgrade so 2x stays crisp on high-DPR phones (transform doesn't re-pick srcset).
     *  Decode a bigger candidate off-DOM first, then widen the visible img's `sizes` — same box, so
     *  it re-selects the (now-cached) candidate with no reflow or visible pop. Once per image. */
    tzUpgradeHiRes() {
      var img = this.zImg;
      if (!img || img.dataset.pgzHi || !img.srcset) return;
      var dpr = window.devicePixelRatio || 1;
      var needPx = Math.round(this.zW * MAX_ZOOM * dpr);
      var probe = new Image();
      probe.decoding = 'async';
      probe.sizes = needPx + 'px';
      probe.srcset = img.srcset;
      if (img.src) probe.src = img.src;
      var apply = function () {
        img.sizes = needPx + 'px';
        img.dataset.pgzHi = '1';
      };
      if (probe.decode) {
        probe.decode().then(apply).catch(function () {});
      } else {
        probe.addEventListener('load', apply);
      }
    }

    /* ------------------------------------------------- smooth main-column scrubbing */

    /** Absolute scrollTop that puts item i at the top of the main column. */
    topFor(i) {
      var item = this.items[clamp(i, 0, this.items.length - 1)];
      if (!item || !this.main) return 0;
      // Measured, not offsetTop-derived: offsetTop is relative to the nearest positioned
      // ancestor, which is only ever `main` by coincidence of the current CSS.
      return (
        item.getBoundingClientRect().top -
        this.main.getBoundingClientRect().top +
        this.main.scrollTop
      );
    }

    /** Re-aim the lerp at item i. Cheap to call every frame — it only moves a number. */
    scrubTo(i) {
      if (!this.main || !this.items.length) return;

      // Clamp to the real scrollable range. An unclamped target past the end (the last item
      // can sit closer to the bottom than one viewport) is unreachable, so `diff` would never
      // shrink and the loop below would spin forever pinning scrollTop.
      var max = Math.max(0, this.main.scrollHeight - this.main.clientHeight);
      this.scrollTarget = clamp(this.topFor(i), 0, max);

      if (DRAG_DAMP >= 1) {
        this.main.scrollTop = this.scrollTarget;
        return;
      }
      this.scrubbing = true;
      if (!this.scrollRAF) this.scrollRAF = requestAnimationFrame(this.scrollTick);
    }

    scrollTick() {
      if (!this.main) return this.stopScrub();

      var cur = this.main.scrollTop;
      var diff = this.scrollTarget - cur;

      // Settled. Threshold is 1px, not sub-pixel — see the dead-zone note below.
      if (Math.abs(diff) < 1) {
        this.main.scrollTop = this.scrollTarget;
        return this.stopScrub();
      }

      // DEAD-ZONE GUARD. `diff * DAMP` goes sub-pixel while `diff` is still ~1-2px, and
      // scrollTop snaps to device pixels — so the write rounds to no movement at all and the
      // loop can neither converge nor exit. Force a minimum 1px step toward the target.
      var step = diff * DRAG_DAMP;
      if (Math.abs(step) < 1) step = diff > 0 ? 1 : -1;
      this.main.scrollTop = cur + step;

      // NO-PROGRESS GUARD. If scrollTop didn't actually move we're clamped at an end of the
      // range; keeping the loop alive would re-pin the column every frame and make the
      // gallery feel frozen.
      if (this.main.scrollTop === cur) return this.stopScrub();

      this.scrollRAF = requestAnimationFrame(this.scrollTick);
    }

    /**
     * Hard-stop the lerp. Used when something else takes over the main column (close, a
     * thumbnail click, a keyboard jump) so two animations can't drive one scrollTop.
     */
    stopScrub() {
      if (this.scrollRAF) cancelAnimationFrame(this.scrollRAF);
      this.scrollRAF = null;
      this.scrubbing = false;
    }

    updateEdgeScroll() {
      if (!this.rail) return;
      var rect = this.rail.getBoundingClientRect();
      var canScroll = this.rail.scrollHeight > this.rail.clientHeight + 1;
      var dir = 0;

      if (canScroll) {
        if (this.lastPointerY < rect.top + EDGE_ZONE) dir = -1;
        else if (this.lastPointerY > rect.bottom - EDGE_ZONE) dir = 1;
      }

      if (dir === this.edgeDir) return;
      this.edgeDir = dir;
      if (dir === 0) this.stopEdgeScroll();
      else if (!this.edgeRAF) this.edgeRAF = requestAnimationFrame(this.edgeTick);
    }

    edgeTick() {
      if (!this.isDragging || !this.edgeDir || !this.rail) {
        this.edgeRAF = null;
        return;
      }
      var before = this.rail.scrollTop;
      this.rail.scrollTop = before + this.edgeDir * EDGE_SPEED;

      // Hit the end of the rail — stop rather than spin a no-op rAF forever.
      if (this.rail.scrollTop === before) {
        this.edgeDir = 0;
        this.edgeRAF = null;
        return;
      }

      this.updateFromPointer();
      this.edgeRAF = requestAnimationFrame(this.edgeTick);
    }

    stopEdgeScroll() {
      this.edgeDir = 0;
      if (this.edgeRAF) {
        cancelAnimationFrame(this.edgeRAF);
        this.edgeRAF = null;
      }
    }

    /* ------------------------------------------------------------------ navigation */

    /**
     * Scroll the MAIN column to item i (click / keyboard / initial open). Uses the browser's
     * native smooth scroll, which is fine HERE because it fires once toward a fixed target —
     * unlike drag-follow, which re-aims constantly and therefore needs the lerp above.
     */
    goTo(i, behavior) {
      if (!this.main || !this.items || !this.items.length) return;
      var clamped = clamp(i, 0, this.items.length - 1);
      if (!this.items[clamped]) return;

      // Never let the lerp and a native smooth scroll drive scrollTop at the same time.
      this.stopScrub();

      var smooth = behavior === 'smooth';
      if (smooth) {
        // GUARD: during a smooth scroll the spy would fire for every item we pass over,
        // flickering the readout through all of them. Suppress it, and put the readout
        // straight on the destination.
        this.suppressSpy = true;
        this.setActive(clamped);
      }

      this.main.scrollTo({ top: this.topFor(clamped), behavior: smooth ? 'smooth' : 'auto' });
    }

    /**
     * USER INPUT ALWAYS WINS. A wheel/touch on the main column hands control straight back to
     * the user, cancelling any in-flight lerp or native smooth scroll. Without this, scrolling
     * during the ~300ms settle fights the animation and the column springs back.
     * `wheel`/`touchstart` are used precisely because they only ever come from real input —
     * a `scroll` listener can't tell the user apart from our own writes.
     */
    onUserScrollIntent() {
      this.stopScrub();
      this.suppressSpy = false;
      clearTimeout(this.settleTimer);
    }

    /** Release the smooth-scroll guard once the main column has actually stopped moving. */
    onMainScroll() {
      if (!this.suppressSpy) return;
      var self = this;
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(function () {
        self.suppressSpy = false;
      }, SETTLE_MS);
    }

    /**
     * Move the readout. CSS derives the indicator's transform from --pgz-i, so this sets one
     * number rather than doing layout math.
     */
    setActive(i) {
      if (i === this.index && this.indicatorSynced) return;
      this.index = i;
      this.indicatorSynced = true;
      this.style.setProperty('--pgz-i', String(i));

      if (!this.thumbs) return;
      this.thumbs.forEach(function (t, n) {
        t.setAttribute('aria-current', n === i ? 'true' : 'false');
      });

      // Keep the active thumb visible when the rail overflows — but NOT mid-drag, where it
      // would fight the edge auto-scroll for control of rail.scrollTop.
      if (this.isDragging) return;
      var active = this.thumbs[i];
      if (active && this.rail && this.rail.scrollHeight > this.rail.clientHeight) {
        active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    /**
     * Scrollspy. A zero-height band pinned to the middle of the main column: exactly one item
     * crosses it at a time, so "which image is in view" is unambiguous. Works identically for
     * the phone 100vh pager and the free-scrolling desktop column.
     */
    setupSpy() {
      if (!this.main || !this.items.length || typeof IntersectionObserver === 'undefined') return;
      var self = this;
      this.spy = new IntersectionObserver(
        function (entries) {
          // THE GUARD. While the box is driving (drag), our lerp is still settling, or a
          // native smooth scroll is in flight, the spy must not write back — otherwise
          // box → main → spy → box oscillates.
          // `scrubbing` matters most AFTER pointerup: the lerp keeps animating for a few
          // hundred ms, and without it the spy would fire at an intermediate position and
          // yank the readout backward before it lands.
          if (self.isDragging || self.scrubbing || self.suppressSpy) return;
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var i = parseInt(entry.target.getAttribute('data-pgz-index'), 10);
            if (!isNaN(i)) self.setActive(i);
          });
        },
        { root: this.main, rootMargin: '-50% 0px -50% 0px', threshold: 0 }
      );
      this.items.forEach(function (item) {
        self.spy.observe(item);
      });
    }

    teardownSpy() {
      if (this.spy) {
        this.spy.disconnect();
        this.spy = null;
      }
    }

    /** Toggle the up/down scroll-affordance chevrons from the rail's live scroll position:
     *  up shows once scrolled off the top, down hides once the end is reached. */
    refreshScrollHints() {
      if (!this.nav || !this.rail) return;
      var overflowing = this.rail.scrollHeight > this.rail.clientHeight + 1;
      var atTop = this.rail.scrollTop <= 1;
      var atBottom = this.rail.scrollTop + this.rail.clientHeight >= this.rail.scrollHeight - 1;
      this.nav.classList.toggle('can-scroll-up', overflowing && !atTop);
      this.nav.classList.toggle('can-scroll-down', overflowing && !atBottom);
    }

    /* ------------------------------------------------------------------ helpers */

    lockScroll() {
      document.documentElement.classList.add('pgz-open');
    }

    unlockScroll() {
      document.documentElement.classList.remove('pgz-open');
    }

    trapFocus(event) {
      var nodes = Array.prototype.filter.call(this.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('product-gallery-zoom', ProductGalleryZoom);
})();
